import React, { useEffect } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import FilterDropdownHeader from 'components/lib/FilterDropdownHeader';
import GradientDivider from 'components/lib/GradientDivider';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';

import { CMASummaryFilterContext } from './CMASummaryFilterContext';
import CMASummaryViewFilterCategories from './CMASummaryViewFilterCategories';
import CMASummaryViewFilterCompetitorsDisplay from './CMASummaryViewFilterCompetitorsDisplay';
import { CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR, DEPENDENCIES } from '../constants';
import { CMASummaryFilters } from '../types';

const Container = styled.div`
  display: flex;
  flex-direction: column;

  .ant-form-item .ant-form-item-control-input {
    min-height: 0;
  }
`;

const CMASummaryFilterBody = styled.div`
  padding: 20px;
  max-height: 600px;
  overflow: scroll;
`;

const INITIAL_VIEW_FILTER_VALUE = {
  parkingFee: true,
  respiteFee: true,
  additionalFee: true,
  moveInFee: true,
  secondPersonEntranceFee: true,
  deposit: true,
  emergencyPendantFee: true,
  contactInformation: true,
  address: true,
  website: true,
  distance: true,
  phone: true,
  yearOpened: true,
  communityNotes: true,
  reviews: true,
  contractBenefits: true,
  careTypes: true,
  ratesFees: true,
  roomType: true,
  communityFee: true,
  petFee: true,
  secondPersonFee: true,
  careFee: true,
  medicationFee: true,
  feeNote: true,
  medicationFeeNote: true,
  incentives: true,
  averageCompetitors: true,
  competitorDisplayMode: CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR.DISPLAY_ALL,
};

type Props = {
  onClose: () => void;
};

const CMASummaryViewFilterDropdown = ({ onClose }: Props) => {
  const [form] = Form.useForm();
  const { competitorId, viewFilters, setViewFilters } = useCommunityViewContext();
  const filters = viewFilters as CMASummaryFilters;
  const getInitialValues = () => ({
    ...INITIAL_VIEW_FILTER_VALUE,
    averageCompetitors: competitorId ? false : INITIAL_VIEW_FILTER_VALUE.averageCompetitors,
  });

  useEffect(() => {
    if (filters?.viewOptions) {
      form.setFieldsValue({
        contactInformation: filters?.viewOptions.contactInformation,
        address: filters?.viewOptions.address,
        website: filters?.viewOptions.website,
        distance: filters?.viewOptions.distance,
        phone: filters?.viewOptions.phone,
        yearOpened: filters?.viewOptions.yearOpened,
        communityNotes: filters?.viewOptions.communityNotes,
        reviews: filters?.viewOptions.reviews,
        contractBenefits: filters?.viewOptions.contractBenefits,
        shopDate: filters?.viewOptions.shopDate,
        occupancy: filters?.viewOptions.occupancy,
        careTypes: filters?.viewOptions.careTypes,
        ratesFees: filters?.viewOptions.ratesFees,
        roomType: filters?.viewOptions.roomType,
        squareFootage: filters?.viewOptions.squareFootage,
        pricePerSquareFootage: filters?.viewOptions.pricePerSquareFootage,
        communityFee: filters?.viewOptions.communityFee,
        petFee: filters?.viewOptions.petFee,
        secondPersonFee: filters?.viewOptions.secondPersonFee,
        parkingFee: filters?.viewOptions.parkingFee,
        respiteFee: filters?.viewOptions.respiteFee,
        additionalFee: filters?.viewOptions.additionalFee,
        moveInFee: filters?.viewOptions.moveInFee,
        secondPersonEntranceFee: filters?.viewOptions.secondPersonEntranceFee,
        deposit: filters?.viewOptions.deposit,
        emergencyPendantFee: filters?.viewOptions.emergencyPendantFee,
        careFee: filters?.viewOptions.careFee,
        medicationFee: filters?.viewOptions.medicationFee,
        feeNote: filters?.viewOptions.feeNote,
        medicationFeeNote: filters?.viewOptions.medicationFeeNote,
        incentives: filters?.viewOptions.incentives,
        averageCompetitors: competitorId
          ? false
          : filters?.viewOptions.averageCompetitors ?? INITIAL_VIEW_FILTER_VALUE.averageCompetitors,
        valueDisplay: filters?.viewOptions.valueDisplay,
        competitorDisplayMode: filters?.viewOptions.competitorDisplayMode,
      });
    }
  }, [filters]);

  const handleSubmit = (values: any) => {
    setViewFilters((prevState: any) => ({
      ...prevState,
      viewOptions: {
        contactInformation: values.contactInformation,
        address: values.address,
        website: values.website,
        distance: values.distance,
        phone: values.phone,
        yearOpened: values.yearOpened,
        communityNotes: values.communityNotes,
        reviews: values.reviews,
        contractBenefits: values.contractBenefits,
        careTypes: values.careTypes,
        ratesFees: values.ratesFees,
        shopDate: values.shopDate,
        occupancy: values.occupancy,
        roomType: values.roomType,
        squareFootage: values.squareFootage,
        pricePerSquareFootage: values.pricePerSquareFootage,
        communityFee: values.communityFee,
        petFee: values.petFee,
        secondPersonFee: values.secondPersonFee,
        parkingFee: values.parkingFee,
        respiteFee: values.respiteFee,
        additionalFee: values.additionalFee,
        moveInFee: values.moveInFee,
        secondPersonEntranceFee: values.secondPersonEntranceFee,
        deposit: values.deposit,
        emergencyPendantFee: values.emergencyPendantFee,
        careFee: values.careFee,
        medicationFee: values.medicationFee,
        feeNote: values.feeNote,
        medicationFeeNote: values.medicationFeeNote,
        incentives: values.incentives,
        averageCompetitors: competitorId ? false : values.averageCompetitors,
        valueDisplay: values.valueDisplay ?? prevState?.viewOptions?.valueDisplay,
        competitorDisplayMode: values.competitorDisplayMode,
      },
    }));
    onClose();
  };

  const handleValuesChange = (changed: any, all: any) => {
    const updates: Record<string, boolean> = {};
    let hasUpdates = false;

    Object.entries(DEPENDENCIES).forEach(([parent, children]) => {
      if (parent in changed) {
        const parentValue = changed[parent];
        children.forEach((child) => {
          if (all[child] !== parentValue) {
            updates[child] = parentValue;
            hasUpdates = true;
          }
        });
        return;
      }

      const childChanged = children.find((c) => c in changed);
      if (!childChanged) return;

      const anyChecked = children.some((c) => all[c]);
      const allUnchecked = !anyChecked;

      if (anyChecked && !all[parent]) {
        updates[parent] = true;
        hasUpdates = true;
      }

      if (allUnchecked && all[parent]) {
        updates[parent] = false;
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      form.setFieldsValue(updates);
    }
  };

  return (
    <Form form={form} initialValues={getInitialValues()} onFinish={handleSubmit} onValuesChange={handleValuesChange}>
      <CMASummaryFilterContext.Provider value={{ form }}>
        <Container>
          <FilterDropdownHeader onReset={() => form.setFieldsValue(getInitialValues())} />
          <CMASummaryFilterBody className="mini-scroll">
            <CMASummaryViewFilterCategories />
            <GradientDivider />
            <CMASummaryViewFilterCompetitorsDisplay />
          </CMASummaryFilterBody>
        </Container>
      </CMASummaryFilterContext.Provider>
    </Form>
  );
};

export default CMASummaryViewFilterDropdown;
